import React, { useState, useEffect } from "react";
import ContentLayout from "@cloudscape-design/components/content-layout";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Container from "@cloudscape-design/components/container";
import Alert from "@cloudscape-design/components/alert";
import Tabs from "@cloudscape-design/components/tabs";
import HelpPanel from "@cloudscape-design/components/help-panel";
import Link from "@cloudscape-design/components/link";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Flashbar from "@cloudscape-design/components/flashbar";
import { useTranslation } from "react-i18next";
import { api } from "../api";
import UsersList from "../components/UsersList";
import AdminsList from "../components/AdminsList";
import LegacyWebhooksList from "../components/LegacyWebhooksList";
import EditLegacyWebhookModal from "../components/EditLegacyWebhookModal";

export default function AdminPage({ setToolsOpen, setToolsContent }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [legacyWebhooks, setLegacyWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flashbarItems, setFlashbarItems] = useState([]);
  const [triggeringProcessor, setTriggeringProcessor] = useState(false);
  const [triggeringNotifier, setTriggeringNotifier] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Set up help panel content
    setToolsContent(
      <HelpPanel header={<h2>{t("help.admin.title")}</h2>}>
        <SpaceBetween size="m">
          <Box>
            <h3>{t("help.admin.users-tab")}</h3>
            <p>{t("help.admin.users-intro")}</p>

            <h4>{t("help.admin.metrics-title")}</h4>
            <ul>
              <li>
                <strong>{t("help.admin.metric-total-users")}</strong> {t("help.admin.metric-total-users-desc")}
              </li>
              <li>
                <strong>{t("help.admin.metric-total-feeds")}</strong> {t("help.admin.metric-total-feeds-desc")}
              </li>
              <li>
                <strong>{t("help.admin.metric-slack-integrations")}</strong> {t("help.admin.metric-slack-integrations-desc")}
              </li>
              <li>
                <strong>{t("help.admin.metric-news-items")}</strong> {t("help.admin.metric-news-items-desc")}
              </li>
            </ul>

            <h4>{t("help.admin.user-columns-title")}</h4>
            <ul>
              <li>
                <strong>{t("help.admin.column-email")}</strong> {t("help.admin.column-email-desc")}
              </li>
              <li>
                <strong>{t("help.admin.column-total-feeds")}</strong> {t("help.admin.column-total-feeds-desc")}
              </li>
              <li>
                <strong>{t("help.admin.column-enabled-feeds")}</strong> {t("help.admin.column-enabled-feeds-desc")}
              </li>
              <li>
                <strong>{t("help.admin.column-slack-enabled")}</strong> {t("help.admin.column-slack-enabled-desc")}
              </li>
              <li>
                <strong>{t("help.admin.column-news-items")}</strong> {t("help.admin.column-news-items-desc")}
              </li>
              <li>
                <strong>{t("help.admin.column-last-updated")}</strong> {t("help.admin.column-last-updated-desc")}
              </li>
            </ul>
          </Box>

          <Box>
            <h3>{t("help.admin.admins-tab")}</h3>
            <p>{t("help.admin.admins-intro")}</p>

            <h4>{t("help.admin.adding-admins")}</h4>
            <ol>
              <li>{t("help.admin.add-step1")}</li>
              <li>{t("help.admin.add-step2")}</li>
              <li>{t("help.admin.add-step3")}</li>
            </ol>

            <h4>{t("help.admin.removing-admins")}</h4>
            <p>{t("help.admin.remove-desc")}</p>

            <Box variant="p">
              <strong>{t("help.admin.permissions-title")}</strong> {t("help.admin.permissions-desc")}
            </Box>
          </Box>

          <Box>
            <h3>{t("help.admin.legacy-tab")}</h3>
            <p>{t("help.admin.legacy-intro")}</p>

            <h4>{t("help.admin.legacy-metrics-title")}</h4>
            <ul>
              <li>
                <strong>{t("help.admin.legacy-metric-total")}</strong> {t("help.admin.legacy-metric-total-desc")}
              </li>
              <li>
                <strong>{t("help.admin.legacy-metric-enabled")}</strong> {t("help.admin.legacy-metric-enabled-desc")}
              </li>
              <li>
                <strong>{t("help.admin.legacy-metric-filters")}</strong> {t("help.admin.legacy-metric-filters-desc")}
              </li>
              <li>
                <strong>{t("help.admin.legacy-metric-news")}</strong> {t("help.admin.legacy-metric-news-desc")}
              </li>
            </ul>

            <Box variant="p">
              <strong>{t("help.admin.legacy-note-title")}</strong> {t("help.admin.legacy-note-desc")}
            </Box>
          </Box>
        </SpaceBetween>
      </HelpPanel>,
    );
  }, [setToolsContent, t]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [usersResult, adminsResult, webhooksResult] = await Promise.all([api.getUserStats(), api.getAdmins(), api.getLegacyWebhookStats()]);
      setUsers(usersResult.users || []);
      setAdmins(adminsResult.admins || []);
      setLegacyWebhooks(webhooksResult.webhooks || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (email) => {
    try {
      await api.addAdmin(email);
      loadData();
    } catch (err) {
      throw err;
    }
  };

  const handleRemoveAdmin = async (email) => {
    try {
      await api.removeAdmin(email);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const showFlash = (type, msg) => {
    setFlashbarItems([{ type, content: msg, dismissible: true, onDismiss: () => setFlashbarItems([]) }]);
  };

  const handleTriggerFeedProcessor = async () => {
    setTriggeringProcessor(true);
    try {
      const result = await api.triggerFeedProcessor();
      showFlash("success", result.message);
    } catch (err) {
      showFlash("error", err.message);
    } finally {
      setTriggeringProcessor(false);
    }
  };

  const handleTriggerSlackNotifier = async () => {
    setTriggeringNotifier(true);
    try {
      const result = await api.triggerSlackNotifier();
      showFlash("success", result.message);
    } catch (err) {
      showFlash("error", err.message);
    } finally {
      setTriggeringNotifier(false);
    }
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          info={
            <Link variant="info" onFollow={() => setToolsOpen(true)}>
              {t("help.action.info")}
            </Link>
          }
        >
          {t("admin.main.title")}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError("")}>
            {error}
          </Alert>
        )}
        <Flashbar items={flashbarItems} />

        <Tabs
          tabs={[
            {
              id: "users",
              label: t("admin.section.users"),
              content: <UsersList users={users} loading={loading} onRefresh={loadData} />,
            },
            {
              id: "legacy-webhooks",
              label: t("admin.section.legacy-webhooks"),
              content: <LegacyWebhooksList webhooks={legacyWebhooks} loading={loading} onRefresh={loadData} onEdit={setEditingWebhook} />,
            },
            {
              id: "admins",
              label: t("admin.section.administrators"),
              content: <AdminsList admins={admins} loading={loading} onAdd={handleAddAdmin} onRemove={handleRemoveAdmin} />,
            },
            {
              id: "system",
              label: t("admin.section.system-actions"),
              content: (
                <Container header={<Header variant="h2">{t("admin.section.system-actions")}</Header>}>
                  <SpaceBetween size="l">
                    <Box>
                      <SpaceBetween size="xs">
                        <Box variant="h3">{t("admin.system.feed-processor-title")}</Box>
                        <Box color="text-body-secondary">{t("admin.system.feed-processor-desc")}</Box>
                        <Button
                          variant="primary"
                          loading={triggeringProcessor}
                          onClick={handleTriggerFeedProcessor}
                          iconName="refresh"
                        >
                          {t("admin.system.trigger-feed-processor")}
                        </Button>
                      </SpaceBetween>
                    </Box>
                    <Box>
                      <SpaceBetween size="xs">
                        <Box variant="h3">{t("admin.system.slack-notifier-title")}</Box>
                        <Box color="text-body-secondary">{t("admin.system.slack-notifier-desc")}</Box>
                        <Button
                          loading={triggeringNotifier}
                          onClick={handleTriggerSlackNotifier}
                          iconName="notification"
                        >
                          {t("admin.system.trigger-slack-notifier")}
                        </Button>
                      </SpaceBetween>
                    </Box>
                  </SpaceBetween>
                </Container>
              ),
            },
          ]}
        />
      </SpaceBetween>

      <EditLegacyWebhookModal
        visible={Boolean(editingWebhook)}
        webhook={editingWebhook}
        onDismiss={() => setEditingWebhook(null)}
        onSaved={() => {
          setEditingWebhook(null);
          showFlash("success", t("admin.legacy.save-success"));
          loadData();
        }}
      />
    </ContentLayout>
  );
}
